import React from "react";
import {
  Typography,
  Box,
  ListItemText,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";
import { ApproverDeleteButton } from "./ApproverDeleteButton";
import { ApproverEdit } from "./ApproverEdit";
import { useApproversByTypeQuery } from "../../../../../services/ticketEasyService/ticketEasy-api";
import { useCenterListQuery } from "../../../../../services/orgService/org-api";

interface FacilityApproversListProps {
  onAddClick: () => void;
}

export const FacilityApproversList: React.FC<FacilityApproversListProps> = ({ onAddClick }) => {
  const { data: centers = [] } = useCenterListQuery();
  const { data: approvers = [], error } = useApproversByTypeQuery("Facility");
  
  const hasApprovers = centers.some(center => 
    approvers.some(approver => approver.locations.includes(center.name))
  );

  return (
    <>
      <Typography variant="h6" gutterBottom align="center">
        Facility Approvers
      </Typography>

      <Paper elevation={0} sx={{ maxHeight: 'calc(100vh - 250px)', overflow: 'auto', backgroundColor: 'transparent' }}>
        {!hasApprovers && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography color="textSecondary">No facility approvers configured</Typography>
          </Box>
        )}
        
        {centers.map((center) => {
          const approversForCenter = approvers.filter((approver) =>
            approver.locations.includes(center.name)
          );

          if (approversForCenter.length === 0) return null;

          return (
            <Box
              key={center.id}
              sx={{
                marginBottom: 3,
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                padding: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#e0e0e0",
                  padding: 1,
                  borderRadius: 1,
                  marginBottom: 2,
                }}
              >
                <Typography variant="subtitle1" gutterBottom align="center">
                  {center.name}
                </Typography>
              </Box>
              {approversForCenter.map((approver) => (
                <div
                  key={approver.profileId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid #eee",
                    padding: "8px",
                  }}
                >
                  <ListItemText
                    primary={
                      <>
                        {approver.name}
                        {approver.isPrimary && (
                          <Tooltip title="Primary Approver">
                            <StarIcon
                              color="primary"
                              fontSize="inherit"
                              style={{
                                marginLeft: 8,
                                fontSize: "1rem",
                                verticalAlign: "text-top",
                              }}
                            />
                          </Tooltip>
                        )}
                      </>
                    }
                    secondary={`Email: ${approver.email}`}
                  />
                  <ApproverEdit approver={approver} />
                  <ApproverDeleteButton approverId={approver.id} />
                </div>
              ))}
            </Box>
          );
        })}
      </Paper>
      
      <Box textAlign="right" sx={{ marginTop: 2 }}>
        <IconButton onClick={onAddClick} color="primary" aria-label="Add Facility Approver">
          <AddIcon />
        </IconButton>
      </Box>
    </>
  );
};
